
import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { Subject, takeUntil } from 'rxjs';

import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { CheckboxStateService } from "../../../core-module/services/state/checkbox-state.service";
import { FeatureDefaultsStateService } from "../../../core-module/services/state/feature-defaults-state.service";
import { InputErrorsStateService } from "../../../core-module/services/state/input-errors-state.service";
import { FeatureUtilsService } from "../../../core-module/services/state/feature-utils.service";
@Component({
  standalone: false,
  selector: 'app-dali102and207',
  templateUrl: './dali102and207.component.html',
  styleUrls: ['./dali102and207.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Dali102and207Component extends FieldType implements OnInit, OnChanges, OnDestroy {
  dimmingCurveType = 'Logarithmic'; // Default from schema
  defaultColor = true;
  useLastStoredLevel = false;
  dontChangeExistingLevel = false;
  isIncludedInConfiguration = false;
  // Track which field groups have been modified (replaces individual enable checkboxes)
  private modifiedFields = new Set<string>();
  private ignoreParentUpdate = false;
  private isEdited = false;
  private hasLocalStorageData = false; // Track if data came from localStorage with enable flags
  // Default values from schema for restore functionality
  private defaultDimmingCurveType: string;
  private defaultMinLevel: number;
  private defaultMaxLevel: number;
  private defaultPowerOnLevel: number;
  private defaultSystemFailureLevel: number;
  private defaultFadeRate: number;
  private defaultFadeTime: number;
  private defaultFastFadeTime: number;
  private defaultExtendedFadeTime: number;
  private defaultOperatingMode: number;
  private defaultScenes: number[] = new Array(16);
  private readonly destroy$ = new Subject<void>();
  // Properties from schema
  properties: LegacyAny;
  // Power Level values
  minLevel: number;
  maxLevel: number;
  powerOnLevel: number;
  systemFailureLevel: number;
  // Validation ranges from schema
  minLevelMin: number;
  minLevelMax: number;
  maxLevelMin: number;
  maxLevelMax: number;
  powerOnLevelMin: number;
  powerOnLevelMax: number;
  systemFailureLevelMin: number;
  systemFailureLevelMax: number;
  sceneMin: number;
  sceneMax: number;
  // Error flags
  minLevelError = false;
  maxLevelError = false;
  powerOnLevelError = false;
  systemFailureLevelError = false;
  sceneErrors: boolean[] = new Array(16).fill(false);
  // Fade values
  fadeRate: number;
  fadeTime: number;
  fastFadeTime: number;
  extendedFadeTime: number;
  extendedFadeTimeBase: number;
  extendedFadeTimeMultiplier: number;
  selectedFadeType = 'extendedFadeTime'; // Track which fade type is selected (default to extended)
  // Fade enum options from schema
  fadeRateOptions: number[] = [];
  fadeTimeOptions: number[] = [];
  fastFadeTimeOptions: number[] = [];
  extendedFadeTimeOptions: number[] = [];
  // Operating Mode
  operatingMode: number;
  operatingModeOptions: number[] = [];
  pathVal: string;
  // Scene values (0-15)
  scenes: number[] = new Array(16);
  // Percentage values calculated based on dimming curve type
  minLevelPercentage: number | null = 0;
  maxLevelPercentage: number | null = 0;
  powerOnLevelPercentage: number | null = 0;
  systemFailureLevelPercentage: number | null = 0;
  // Extended Fade Time formatted options
  extendedFadeTimes: string[] = [];
  constructor(
    private readonly ref: ChangeDetectorRef, private readonly appState: AppStateService,
    private featureDefaults: FeatureDefaultsStateService,
    private checkboxState: CheckboxStateService,
    private inputErrors: InputErrorsStateService,
    private utils: FeatureUtilsService
  ) {
    super();
  }
  ngOnInit(): void {
    // Initialize image path for info icon
    this.pathVal = this.utils.getImageUrl(window.location.hostname);
    
    // Protect initial load from parent checkbox updates
    this.ignoreParentUpdate = true;

    // Initialize with defaults first
    this.loadSchemaProperties();

    const configData = this.appState.getConfigurationData();
    let localStorageValue = null;

    // Check UnSelectedFeatConfig first (for edited but unselected features)
    const unSelectedFeat = this.appState.getUnSelectedFeatConfig();
    let isConfigEdited = false;

    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex = 0;
      unSelectedFeat.forEach((element) => {
        if (element["featureName"] && element["featureName"].toString().toUpperCase() === "DALICONFIGURATION102AND207") {
          isConfigEdited = true;
          localStorageValue = element["featureParameters"][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appState.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    // Use centralized method to get feature data (prioritizes SaveSetData over ConfigurationData)
    if (!isConfigEdited) {
      const daliConfig102and207Data = this.appState.getFeatureData('DaliConfiguration102and207') ||
        this.appState.getFeatureData('daliconfiguration102and207');
      if (daliConfig102and207Data) {
        localStorageValue = daliConfig102and207Data;
        // If data exists in localStorage, it means user previously configured it (refresh scenario)
        // This distinguishes refresh from read device flow
        this.hasLocalStorageData = Object.keys(localStorageValue).length > 0;
      }
    }

    // Load values from localStorage or formControl
    let currentValue = localStorageValue || this.formControl.value || {};
    this.loadValuesFromStorage(currentValue);

    // Revalidate all fields after loading from storage to restore error states
    this.revalidateAllFields();


    // Subscribe to restore default trigger
    this.featureDefaults.getDali102and207ConfigurationDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === "valid" && !this.ignoreParentUpdate) {
          this.defaultColor = true;
          this.resetToDefaults();
        }
      });

    // Subscribe to parent "Include in configuration" checkbox changes
    this.checkboxState.getDali102and207ConfigurationParentCheckbox
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        // Handle unchecking - always clear modified fields and reset hasLocalStorageData
        if (state !== null && !this.ignoreParentUpdate && !state.checked) {
          this.isIncludedInConfiguration = false;
          this.modifiedFields.clear();
          this.hasLocalStorageData = false; // Reset for next interaction
          currentValue = {}; // Clear currentValue so next check enables all checkboxes
          this.inputErrors.sendDali102and207ConfigurationInputError(false);
          this.ref.detectChanges();
          return;
        }

        // Handle checking - only respond if not ignoring updates and no localStorage enable flags
        if (state !== null && !this.ignoreParentUpdate && !this.hasLocalStorageData && state.checked) {
          this.isIncludedInConfiguration = true;

          // Check if we have LegacyAny meaningful data (Import DCF) or not (Find and Select)
          const hasDimmingCurve = currentValue.DimmingCurve !== undefined;
          const hasMinMaxLevel = currentValue.MinLevel !== undefined || currentValue.MaxLevel !== undefined;
          const hasPowerOnLevel = currentValue.PowerOnLevel !== undefined;
          const hasSystemFailureLevel = currentValue.SystemFailureLevel !== undefined;
          const hasFadeRate = currentValue.FadeRate !== undefined;
          const hasFadeTime = currentValue.FadeTime !== undefined ||
            currentValue.FastFadeTime !== undefined ||
            currentValue.ExtendedFadeTime !== undefined;
          const hasOperatingMode = currentValue.OperatingMode !== undefined;
          const hasScenes = currentValue.Scene0 !== undefined ||
            currentValue.Scene1 !== undefined ||
            currentValue.Scene2 !== undefined ||
            currentValue.Scene3 !== undefined ||
            currentValue.Scene4 !== undefined ||
            currentValue.Scene5 !== undefined ||
            currentValue.Scene6 !== undefined ||
            currentValue.Scene7 !== undefined ||
            currentValue.Scene8 !== undefined ||
            currentValue.Scene9 !== undefined ||
            currentValue.Scene10 !== undefined ||
            currentValue.Scene11 !== undefined ||
            currentValue.Scene12 !== undefined ||
            currentValue.Scene13 !== undefined ||
            currentValue.Scene14 !== undefined ||
            currentValue.Scene15 !== undefined;

          const hasAnyData = hasDimmingCurve || hasMinMaxLevel || hasPowerOnLevel ||
            hasSystemFailureLevel || hasFadeRate || hasFadeTime || hasOperatingMode || hasScenes;

          if (hasAnyData) {
            // Import DCF flow - selective marking based on available data
            if (hasDimmingCurve){ this.modifiedFields.add('dimmingCurve'); }
            if (hasMinMaxLevel){ this.modifiedFields.add('minMaxLevel'); }
            if (hasPowerOnLevel){ this.modifiedFields.add('powerOnLevel'); }
            if (hasSystemFailureLevel){ this.modifiedFields.add('systemFailureLevel'); }
            if (hasFadeRate){ this.modifiedFields.add('fadeRate'); }
            if (hasFadeTime){ this.modifiedFields.add('fadeTime'); }
            if (hasOperatingMode){ this.modifiedFields.add('operatingMode'); }
            if (hasScenes){ this.modifiedFields.add('scenes'); }
          } else {
            // Find and Select flow - mark all fields as modified
            this.modifiedFields.add('dimmingCurve');
            this.modifiedFields.add('minMaxLevel');
            this.modifiedFields.add('powerOnLevel');
            this.modifiedFields.add('systemFailureLevel');
            this.modifiedFields.add('fadeRate');
            this.modifiedFields.add('fadeTime');
            this.modifiedFields.add('operatingMode');
            this.modifiedFields.add('scenes');
          }

          // Update form control and save to localStorage with the enabled states
          this.updateFormControl();
          // Persist current form values so save-feature-file has a payload
          // even if the user never edits any field.
          this.setConfigurationData();
          this.sendErrorStateToParent();
          this.ref.detectChanges();
        }
      });

    // Update form control with loaded values
    this.updateFormControl();

    // Enable parent checkbox if LegacyAny field has been modified
    if (this.modifiedFields.size > 0) {
      this.checkboxState.enableDali102and207Checkbox();
      this.isIncludedInConfiguration = true;
    }

    // Initialize error state
    this.sendErrorStateToParent();

    this.checkDefaults();

    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }
  ngOnChanges(changes: SimpleChanges): void {
    // When field input changes (after API response), reload schema properties
    if (changes['field'] && !changes['field'].firstChange) {
      this.loadSchemaProperties();
    }
  }
  private loadSchemaProperties(): void {
    // Get schema properties from field - field is an array containing the schema
    // The schema is passed as [field]="dali102and207ConfigurationSchema" from parent
    let schemaObject = null;
    if (Array.isArray(this.field) && this.field.length > 0) {
      schemaObject = this.field[0]; // Get first element of array
    } else if (this.field && typeof this.field === 'object' && !Array.isArray(this.field)) {
      schemaObject = this.field;
    }
    // Properties are directly on the schema object
    this.properties = schemaObject?.properties;
    // Initialize default values from schema
    this.initializeDefaults();
  }
  private initializeDefaults(): void {
    if (!this.properties) {
      return;
    }
    // Get default values ONLY from schema properties
    this.dimmingCurveType = this.properties.DimmingCurve?.default;
    this.defaultDimmingCurveType = this.dimmingCurveType;
    // Power Levels
    this.minLevel = this.properties.MinLevel?.default;
    this.defaultMinLevel = this.minLevel;
    this.maxLevel = this.properties.MaxLevel?.default;
    this.defaultMaxLevel = this.maxLevel;
    this.powerOnLevel = this.properties.PowerOnLevel?.default;
    this.defaultPowerOnLevel = this.powerOnLevel;
    this.systemFailureLevel = this.properties.SystemFailureLevel?.default;
    this.defaultSystemFailureLevel = this.systemFailureLevel;
    // Get validation ranges from schema
    this.minLevelMin = this.properties.MinLevel?.minimum ?? 0;
    this.minLevelMax = 254;
    this.maxLevelMin = 0;
    this.maxLevelMax = this.properties.MaxLevel?.maximum ?? 254;
    this.powerOnLevelMin = this.properties.PowerOnLevel?.minimum ?? 0;
    this.powerOnLevelMax = this.properties.PowerOnLevel?.maximum ?? 255;
    this.systemFailureLevelMin = this.properties.SystemFailureLevel?.minimum ?? 0;
    this.systemFailureLevelMax = this.properties.SystemFailureLevel?.maximum ?? 255;
    this.sceneMin = this.properties.Scene0?.minimum ?? 0;
    this.sceneMax = this.properties.Scene0?.maximum ?? 255;
    // Populate enum options FIRST before setting default values
    this.fadeRateOptions = this.properties.FadeRate?.enum || [];
    this.fadeTimeOptions = this.properties.FadeTime?.enum || [];
    this.fastFadeTimeOptions = this.properties.FastFadeTime?.enum || [];
    this.extendedFadeTimeOptions = this.properties.ExtendedFadeTime?.enum || [];
    // Generate Operating Mode options (0 or 128-255 based on schema)
    this.operatingModeOptions = [0];
    for (let i = 128; i <= 255; i++) {
      this.operatingModeOptions.push(i);
    }
    // THEN set fade default values after options are populated
    this.fadeRate = this.properties.FadeRate?.default;
    this.defaultFadeRate = this.fadeRate;
    this.fadeTime = this.properties.FadeTime?.default;
    this.defaultFadeTime = this.fadeTime;
    this.fastFadeTime = this.properties.FastFadeTime?.default;
    this.defaultFastFadeTime = this.fastFadeTime;
    this.extendedFadeTime = this.properties.ExtendedFadeTime?.default;
    this.defaultExtendedFadeTime = this.extendedFadeTime;
    // Operating Mode default value
    this.operatingMode = this.properties.OperatingMode?.default;
    this.defaultOperatingMode = this.operatingMode;
    // Scenes (Scene0 through Scene15)
    for (let i = 0; i < 16; i++) {
      const sceneKey = `Scene${i}`;
      const defaultValue = this.properties[sceneKey]?.default;
      this.scenes[i] = defaultValue;
      this.defaultScenes[i] = defaultValue;
    }
    // Calculate initial percentages
    this.calculateAllPercentages();
  }

  private loadValuesFromStorage(currentValue: LegacyAny): void {
    // Load values from localStorage/formControl if they exist
    // This handles 3 flows:
    // 1. Read device: only values, no enable flags -> checkboxes stay false, values populate
    //    - feature-supported calls notifyDali102and207ConfigurationParentCheckbox(false) or nothing
    // 2. Import DCF: only values, no enable flags -> checkboxes enabled via parent notification
    //    - feature-supported calls notifyDali102and207ConfigurationParentCheckbox(true)
    // 3. Refresh: values + enable flags persisted -> checkbox state restored from localStorage
    if (!currentValue || Object.keys(currentValue).length === 0) {
      return;
    }

    this.loadDimmingCurve(currentValue);
    this.loadMinMaxLevels(currentValue);
    this.loadPowerOnLevel(currentValue);
    this.loadSystemFailureLevel(currentValue);
    this.loadFadeRate(currentValue);
    this.loadFadeTimes(currentValue);
    this.loadOperatingMode(currentValue);
    this.loadScenes(currentValue);

    this.calculateAllPercentages();
  }

  private loadDimmingCurve(currentValue: LegacyAny): void {
    if (currentValue.DimmingCurve !== undefined) {
      this.dimmingCurveType = currentValue.DimmingCurve;
      // If data exists in storage, mark as modified (for parent checkbox)
      if (this.hasLocalStorageData) {
        this.modifiedFields.add('dimmingCurve');
      }
    }
  }

  private loadMinMaxLevels(currentValue: LegacyAny): void {
    if (currentValue.MinLevel !== undefined || currentValue.MaxLevel !== undefined) {
      if (currentValue.MinLevel !== undefined) {
        this.minLevel = currentValue.MinLevel;
      }
      if (currentValue.MaxLevel !== undefined) {
        this.maxLevel = currentValue.MaxLevel;
      }
      // If data exists in storage, mark as modified (for parent checkbox)
      if (this.hasLocalStorageData) {
        this.modifiedFields.add('minMaxLevel');
      }
    }
  }

  private loadPowerOnLevel(currentValue: LegacyAny): void {
    if (currentValue.PowerOnLevel !== undefined || currentValue.UseLastStoredLevel !== undefined) {
      if ('PowerOnLevel' in currentValue) {
        this.powerOnLevel = currentValue.PowerOnLevel;
      }
      if (currentValue.UseLastStoredLevel !== undefined) {
        this.useLastStoredLevel = currentValue.UseLastStoredLevel;
      } else if (currentValue.PowerOnLevel === 255) {
        this.useLastStoredLevel = true;
      }
      // If data exists in storage, mark as modified (for parent checkbox)
      if (this.hasLocalStorageData) {
        this.modifiedFields.add('powerOnLevel');
      }
    }
  }

  private loadSystemFailureLevel(currentValue: LegacyAny): void {
    if (currentValue.SystemFailureLevel !== undefined || currentValue.DontChangeExistingLevel !== undefined) {
      if ('SystemFailureLevel' in currentValue) {
        this.systemFailureLevel = currentValue.SystemFailureLevel;
      }
      if (currentValue.DontChangeExistingLevel !== undefined) {
        this.dontChangeExistingLevel = currentValue.DontChangeExistingLevel;
      } else if (currentValue.SystemFailureLevel === 255) {
        this.dontChangeExistingLevel = true;
      }
      // If data exists in storage, mark as modified (for parent checkbox)
      if (this.hasLocalStorageData) {
        this.modifiedFields.add('systemFailureLevel');
      }
    }
  }

  private loadFadeRate(currentValue: LegacyAny): void {
    if (currentValue.FadeRate !== undefined) {
      this.fadeRate = currentValue.FadeRate;
      // If data exists in storage, mark as modified (for parent checkbox)
      if (this.hasLocalStorageData) {
        this.modifiedFields.add('fadeRate');
      }
    }
  }

  private loadFadeTimes(currentValue: LegacyAny): void {
    if (currentValue.FadeTime !== undefined || currentValue.FastFadeTime !== undefined || currentValue.ExtendedFadeTime !== undefined) {
      if (currentValue.FadeTime !== undefined) {
        this.fadeTime = currentValue.FadeTime;
      }
      if (currentValue.FastFadeTime !== undefined) {
        this.fastFadeTime = currentValue.FastFadeTime;
      }
      if (currentValue.ExtendedFadeTime !== undefined) {
        this.extendedFadeTime = currentValue.ExtendedFadeTime;
      }

      // Determine selected fade type
      if (currentValue.SelectedFadeType !== undefined) {
        // Refresh scenario - use saved selection
        this.selectedFadeType = currentValue.SelectedFadeType;
      } else {
        if (this.fadeTime !== this.defaultFadeTime) {
          this.selectedFadeType = 'fadeTime';
        } else if (this.fastFadeTime !== this.defaultFastFadeTime) {
          this.selectedFadeType = 'fastFadeTime';
        } else {
          // Either extendedFadeTime is non-default OR all are default
          this.selectedFadeType = 'extendedFadeTime';
        }
      }

      // If data exists in storage, mark as modified (for parent checkbox)
      if (this.hasLocalStorageData) {
        this.modifiedFields.add('fadeTime');
      }
    }
  }

  private loadOperatingMode(currentValue: LegacyAny): void {
    if (currentValue.OperatingMode !== undefined) {
      this.operatingMode = currentValue.OperatingMode;
      // If data exists in storage, mark as modified (for parent checkbox)
      if (this.hasLocalStorageData) {
        this.modifiedFields.add('operatingMode');
      }
    }
  }

  private loadScenes(currentValue: LegacyAny): void {
    let hasScenes = false;
    for (let i = 0; i < 16; i++) {
      const sceneKey = `Scene${i}`;
      if (currentValue[sceneKey] !== undefined) {
        this.scenes[i] = currentValue[sceneKey];
        hasScenes = true;
      }
    }
    if (hasScenes) {
      // If data exists in storage, mark as modified (for parent checkbox)
      if (this.hasLocalStorageData) {
        this.modifiedFields.add('scenes');
      }
    }
  }

  private revalidateAllFields(): void {
    // Revalidate min/max level relationship and absolute ranges
    if (this.minLevel !== undefined && this.maxLevel !== undefined) {
      this.minLevelError = isNaN(this.minLevel) || this.minLevel < this.minLevelMin || this.minLevel > this.maxLevelMax || this.minLevel > this.maxLevel;
      if (this.minLevelError) {
        this.minLevelPercentage = null;
      } else {
        this.minLevelPercentage = this.calculatePercentage(this.minLevel);
      }

      this.maxLevelError = isNaN(this.maxLevel) || this.maxLevel < this.maxLevelMin || this.maxLevel > this.maxLevelMax || this.maxLevel < this.minLevel;
      if (this.maxLevelError) {
        this.maxLevelPercentage = null;
      } else {
        this.maxLevelPercentage = this.calculatePercentage(this.maxLevel);
      }
    }

    if (this.powerOnLevel !== undefined) {
      if (this.useLastStoredLevel) {
        this.powerOnLevelError = false;
        this.powerOnLevelPercentage = null;
      } else {
        // Note: null means NaN was saved to localStorage (JSON converts NaN to null)
        // Treat null as an error
        if (this.powerOnLevel === null) {
          this.powerOnLevelError = true;
          this.powerOnLevelPercentage = null;
        } else {
          this.powerOnLevelError = isNaN(this.powerOnLevel) || this.powerOnLevel < this.powerOnLevelMin || this.powerOnLevel > this.powerOnLevelMax;
          if (this.powerOnLevelError) {
            this.powerOnLevelPercentage = null;
          } else {
            this.powerOnLevelPercentage = this.calculatePercentage(this.powerOnLevel);
          }
        }
      }
    }

    if (this.systemFailureLevel !== undefined) {
      if (this.dontChangeExistingLevel) {
        this.systemFailureLevelError = false;
        this.systemFailureLevelPercentage = null;
      } else {
        // Note: null means NaN was saved to localStorage (JSON converts NaN to null)
        // Treat null as an error
        if (this.systemFailureLevel === null) {
          this.systemFailureLevelError = true;
          this.systemFailureLevelPercentage = null;
        } else {
          this.systemFailureLevelError = isNaN(this.systemFailureLevel) || this.systemFailureLevel < this.systemFailureLevelMin || this.systemFailureLevel > this.systemFailureLevelMax;
          if (this.systemFailureLevelError) {
            this.systemFailureLevelPercentage = null;
          } else {
            this.systemFailureLevelPercentage = this.calculatePercentage(this.systemFailureLevel);
          }
        }
      }
    }

    // Revalidate all scenes
    for (let i = 0; i < 16; i++) {
      // Note: NaN values become null when saved to localStorage via JSON
      // undefined = never touched (but schema provides defaults, so rarely happens)
      // null = user deleted content (was NaN, became null in storage)
      // NaN = user just deleted content (before save)
      if (this.scenes[i] === undefined) {
        this.sceneErrors[i] = false;
      } else if (this.scenes[i] === null) {
        // Treat null as NaN (from localStorage) - this is an error
        this.sceneErrors[i] = true;
      } else {
        this.sceneErrors[i] = isNaN(this.scenes[i]) || this.scenes[i] < this.sceneMin || this.scenes[i] > this.sceneMax;
      }
    }
    this.sendErrorStateToParent();
  }

  onDimmingCurveChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    // Mark dimming curve as modified
    this.modifiedFields.add('dimmingCurve');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.calculateAllPercentages();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onOperatingModeChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    // Mark operating mode as modified
    this.modifiedFields.add('operatingMode');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onFadeRateChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    // Mark fade rate as modified
    this.modifiedFields.add('fadeRate');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onFadeTimeChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    // Automatically select fade time radio button when user changes fade time
    this.selectedFadeType = 'fadeTime';
    // Mark fade time as modified
    this.modifiedFields.add('fadeTime');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onFadeTimeRadioChange(): void {
    this.handleFadeTimeCheckboxEnable();
  }
  onFastFadeTimeChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    // Automatically select fast fade time radio button when user changes fast fade time
    this.selectedFadeType = 'fastFadeTime';
    // Mark fade time as modified
    this.modifiedFields.add('fadeTime');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onFastFadeTimeRadioChange(): void {
    this.handleFadeTimeCheckboxEnable();
  }
  onExtendedFadeTimeChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    // Automatically select extended fade time radio button when user changes extended fade time
    this.selectedFadeType = 'extendedFadeTime';
    // Mark fade time as modified
    this.modifiedFields.add('fadeTime');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onExtendedFadeTimeRadioChange(): void {
    this.handleFadeTimeCheckboxEnable();
  }

  private handleFadeTimeCheckboxEnable(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    // Mark fade time as modified when user selects radio button
    this.modifiedFields.add('fadeTime');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onUseLastStoredLevelChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    if (this.useLastStoredLevel) {
      // Set to mask value 255 and disable input
      this.powerOnLevel = 255;
      this.powerOnLevelError = false;
      this.powerOnLevelPercentage = null;
      // Trigger immediate change detection to update UI
      this.ref.detectChanges();
      // Mark as modified since we're setting a non-default state
      this.modifiedFields.add('powerOnLevel');
    } else {
      // Restore to default value when unchecked
      this.powerOnLevel = this.defaultPowerOnLevel;
      this.powerOnLevelPercentage = this.calculatePercentage(this.powerOnLevel);
      // Don't add to modifiedFields when restoring to default
    }
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onDontChangeExistingLevelChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    if (this.dontChangeExistingLevel) {
      // Set to mask value 255 and disable input
      this.systemFailureLevel = 255;
      this.systemFailureLevelError = false;
      this.systemFailureLevelPercentage = null;
      // Trigger immediate change detection to update UI
      this.ref.detectChanges();
      // Mark as modified since we're setting a non-default state
      this.modifiedFields.add('systemFailureLevel');
    } else {
      // Restore to default value when unchecked
      this.systemFailureLevel = this.defaultSystemFailureLevel;
      this.systemFailureLevelPercentage = this.calculatePercentage(this.systemFailureLevel);
      // Don't add to modifiedFields when restoring to default
    }
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }

  private updateIncludeInConfiguration(): void {
    // Check if LegacyAny field has a non-default value (like DALI102)
    const hasNonDefaultValue = this.hasAnyNonDefaultValue();
    if (hasNonDefaultValue) {
      // When values are non-default, enable and check the checkbox
      this.checkboxState.enableDali102and207Checkbox();
      this.isIncludedInConfiguration = true;
    } else {
      // When values are at defaults, don't automatically uncheck the checkbox
      // Only disable it if it's not currently checked
      // This preserves the user's explicit checkbox state
      if (!this.isIncludedInConfiguration) {
        this.checkboxState.disableDali102and207Checkbox();
      }
      // If it was checked, leave it checked (user explicitly wants defaults included)
    }
  }

  private hasAnyNonDefaultValue(): boolean {
    // Check if LegacyAny field has a non-default value
    const isDimmingCurveNonDefault = this.dimmingCurveType !== this.defaultDimmingCurveType;
    const isMinLevelNonDefault = this.minLevel !== this.defaultMinLevel;
    const isMaxLevelNonDefault = this.maxLevel !== this.defaultMaxLevel;

    // For power on level: consider non-default if useLastStoredLevel is checked OR value is non-default
    const isPowerOnLevelNonDefault = this.useLastStoredLevel || (this.powerOnLevel !== this.defaultPowerOnLevel);

    // For system failure level: consider non-default if dontChangeExistingLevel is checked OR value is non-default
    const isSystemFailureLevelNonDefault = this.dontChangeExistingLevel || (this.systemFailureLevel !== this.defaultSystemFailureLevel);

    const isFadeRateNonDefault = this.fadeRate !== this.defaultFadeRate;
    const isFadeTimeNonDefault = this.fadeTime !== this.defaultFadeTime;
    const isFastFadeTimeNonDefault = this.fastFadeTime !== this.defaultFastFadeTime;
    const isExtendedFadeTimeNonDefault = this.extendedFadeTime !== this.defaultExtendedFadeTime;
    const isSelectedFadeTypeNonDefault = this.selectedFadeType !== 'extendedFadeTime';
    const isOperatingModeNonDefault = this.operatingMode !== this.defaultOperatingMode;

    // Check if LegacyAny scene is non-default
    const areScenesNonDefault = this.scenes.some((scene, index) => scene !== this.defaultScenes[index]);

    return isDimmingCurveNonDefault || isMinLevelNonDefault || isMaxLevelNonDefault ||
      isPowerOnLevelNonDefault || isSystemFailureLevelNonDefault || isFadeRateNonDefault ||
      isFadeTimeNonDefault || isFastFadeTimeNonDefault || isExtendedFadeTimeNonDefault ||
      isSelectedFadeTypeNonDefault || isOperatingModeNonDefault || areScenesNonDefault;
  }
  // Validation methods
  onMinLevelInput(value: string): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    const numValue = parseInt(value, 10);
    this.minLevel = numValue;
    // Min level: check absolute range (minLevelMin-maxLevelMax) and if min > max
    this.minLevelError = isNaN(numValue) || numValue < this.minLevelMin || numValue > this.maxLevelMax || numValue > this.maxLevel;
    if (this.minLevelError) {
      this.minLevelPercentage = null;
    } else {
      this.minLevelPercentage = this.calculatePercentage(numValue);
    }
    // Clear max level error if it was showing relationship error and now relationship is valid
    if (!isNaN(this.maxLevel) && this.maxLevel >= this.maxLevelMin && this.maxLevel <= this.maxLevelMax && this.maxLevel >= numValue) {
      this.maxLevelError = false;
      this.maxLevelPercentage = this.calculatePercentage(this.maxLevel);
    }
    // Mark min/max level as modified when user modifies min level
    this.modifiedFields.add('minMaxLevel');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onMaxLevelInput(value: string): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    const numValue = parseInt(value, 10);
    this.maxLevel = numValue;
    // Max level: check absolute range (maxLevelMin-maxLevelMax) and if max < min
    this.maxLevelError = isNaN(numValue) || numValue < this.maxLevelMin || numValue > this.maxLevelMax || numValue < this.minLevel;
    if (this.maxLevelError) {
      this.maxLevelPercentage = null;
    } else {
      this.maxLevelPercentage = this.calculatePercentage(numValue);
    }
    // Clear min level error if it was showing relationship error and now relationship is valid
    if (!isNaN(this.minLevel) && this.minLevel >= this.minLevelMin && this.minLevel <= this.maxLevelMax && this.minLevel <= numValue) {
      this.minLevelError = false;
      this.minLevelPercentage = this.calculatePercentage(this.minLevel);
    }
    // Mark min/max level as modified when user modifies max level
    this.modifiedFields.add('minMaxLevel');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onPowerOnLevelInput(value: string, event?: LegacyAny): void {
    if (this.useLastStoredLevel) {
      return; // Prevent changes when using last stored level
    }
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    const numValue = parseInt(value, 10);
    this.powerOnLevel = numValue;

    // If user enters 255, automatically check "Use Last Stored Level" checkbox
    if (numValue === 255) {
      this.useLastStoredLevel = true;
      this.powerOnLevelError = false;
      this.powerOnLevelPercentage = null;
      // Trigger change detection to apply disabled state (cursor hidden via CSS)
      this.ref.detectChanges();
    } else {
      this.powerOnLevelError = isNaN(numValue) || numValue < this.powerOnLevelMin || numValue > this.powerOnLevelMax;
      if (this.powerOnLevelError) {
        this.powerOnLevelPercentage = null;
      } else {
        this.powerOnLevelPercentage = this.calculatePercentage(numValue);
      }
    }

    // Mark power on level as modified when user modifies power on level
    this.modifiedFields.add('powerOnLevel');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onSystemFailureLevelInput(value: string, event?: LegacyAny): void {
    if (this.dontChangeExistingLevel) {
      return; // Prevent changes when not changing existing level
    }
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    const numValue = parseInt(value, 10);
    this.systemFailureLevel = numValue;

    // If user enters 255, automatically check "Don't Change Existing Level" checkbox
    if (numValue === 255) {
      this.dontChangeExistingLevel = true;
      this.systemFailureLevelError = false;
      this.systemFailureLevelPercentage = null;
      // Trigger change detection to apply disabled state (cursor hidden via CSS)
      this.ref.detectChanges();
    } else {
      this.systemFailureLevelError = isNaN(numValue) || numValue < this.systemFailureLevelMin || numValue > this.systemFailureLevelMax;
      if (this.systemFailureLevelError) {
        this.systemFailureLevelPercentage = null;
      } else {
        this.systemFailureLevelPercentage = this.calculatePercentage(numValue);
      }
    }

    // Mark system failure level as modified when user modifies system failure level
    this.modifiedFields.add('systemFailureLevel');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  onSceneInput(index: number, value: string): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    const numValue = parseInt(value, 10);
    this.scenes[index] = numValue;
    this.sceneErrors[index] = isNaN(numValue) || numValue < this.sceneMin || numValue > this.sceneMax;
    // Mark scenes as modified when user modifies LegacyAny scene
    this.modifiedFields.add('scenes');
    this.updateIncludeInConfiguration();
    this.updateFormControl();
    this.checkDefaults();
    if (this.isEdited) {
      this.setConfigurationData();
    }
    this.ignoreParentUpdate = false;
  }
  numberOnly(event: LegacyAny): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text');
    if (pastedText && /^\d+$/.test(pastedText)) {
      const target = event.target as HTMLInputElement;
      target.value = pastedText;
      const inputEvent = new Event('input', { bubbles: true });
      target.dispatchEvent(inputEvent);
    }
  }
  checkDefaults(): void {
    const isDimmingCurveDefault = this.dimmingCurveType === this.defaultDimmingCurveType;
    const isMinLevelDefault = this.minLevel === this.defaultMinLevel;
    const isMaxLevelDefault = this.maxLevel === this.defaultMaxLevel;
    const isPowerOnLevelDefault = this.powerOnLevel === this.defaultPowerOnLevel;
    const isSystemFailureLevelDefault = this.systemFailureLevel === this.defaultSystemFailureLevel;
    const isFadeRateDefault = this.fadeRate === this.defaultFadeRate;
    const isFadeTimeDefault = this.fadeTime === this.defaultFadeTime;
    const isFastFadeTimeDefault = this.fastFadeTime === this.defaultFastFadeTime;
    const isExtendedFadeTimeDefault = this.extendedFadeTime === this.defaultExtendedFadeTime;
    const isSelectedFadeTypeDefault = this.selectedFadeType === 'extendedFadeTime';
    const isOperatingModeDefault = this.operatingMode === this.defaultOperatingMode;
    // Check all scenes
    const areScenesDefault = this.scenes.every((scene, index) => scene === this.defaultScenes[index]);
    this.defaultColor = isDimmingCurveDefault && isMinLevelDefault && isMaxLevelDefault &&
      isPowerOnLevelDefault && isSystemFailureLevelDefault && isFadeRateDefault &&
      isFadeTimeDefault && isFastFadeTimeDefault && isExtendedFadeTimeDefault &&
      isSelectedFadeTypeDefault && isOperatingModeDefault && areScenesDefault;
    this.featureDefaults.setDali102and207ConfigurationDefaultColor(this.defaultColor);
    // Send error state to parent
    this.sendErrorStateToParent();
  }
  private sendErrorStateToParent(): void {
    // Check if LegacyAny MODIFIED field has an error (only report errors for modified fields)
    const hasError = (this.modifiedFields.has('minMaxLevel') && (this.minLevelError || this.maxLevelError)) ||
      (this.modifiedFields.has('powerOnLevel') && this.powerOnLevelError) ||
      (this.modifiedFields.has('systemFailureLevel') && this.systemFailureLevelError) ||
      (this.modifiedFields.has('scenes') && this.sceneErrors.some(error => error === true));
    this.inputErrors.sendDali102and207ConfigurationInputError(hasError);
  }
  /**
   * Calculate percentage based on DALI arc power level and dimming curve type
   * @param arcPowerLevel - DALI arc power level (0-255)
   * @returns Light output percentage (rounded to nearest integer) or null if invalid
   */
  private calculatePercentage(arcPowerLevel: number): number | null {
    if (isNaN(arcPowerLevel)) {
      return null;
    }
    let percentage: number;
    if (this.dimmingCurveType === 'Linear') {
      // Linear calculation: light output (%) = a/(b - c) * 100
      // where a = DALI arc power level, b = 254, c = 0
      percentage = (arcPowerLevel / 254) * 100;
    } else {
      // Logarithmic calculation: light output (%) = 10^((a-1)/b - 1)
      // where a = DALI arc power level, b = 253/3
      const b = 253 / 3;
      percentage = Math.pow(10, ((arcPowerLevel - 1) / b) - 1);
    }
    // Cap percentage at 100% and round to nearest whole number
    return Math.round(Math.min(percentage, 100));
  }
  /**
   * Recalculate all percentages when dimming curve type changes
   */
  private calculateAllPercentages(): void {
    this.minLevelPercentage = this.minLevelError ? null : this.calculatePercentage(this.minLevel);
    this.maxLevelPercentage = this.maxLevelError ? null : this.calculatePercentage(this.maxLevel);
    this.powerOnLevelPercentage = this.powerOnLevelError ? null : this.calculatePercentage(this.powerOnLevel);
    this.systemFailureLevelPercentage = this.systemFailureLevelError ? null : this.calculatePercentage(this.systemFailureLevel);
  }
  resetToDefaults(): void {
    // Reset all values to their schema defaults
    this.dimmingCurveType = this.defaultDimmingCurveType;
    this.minLevel = this.defaultMinLevel;
    this.maxLevel = this.defaultMaxLevel;
    this.powerOnLevel = this.defaultPowerOnLevel;
    this.systemFailureLevel = this.defaultSystemFailureLevel;
    this.fadeRate = this.defaultFadeRate;
    this.fadeTime = this.defaultFadeTime;
    this.fastFadeTime = this.defaultFastFadeTime;
    this.extendedFadeTime = this.defaultExtendedFadeTime;
    this.selectedFadeType = 'extendedFadeTime';
    this.operatingMode = this.defaultOperatingMode;
    // Reset all scenes
    for (let i = 0; i < 16; i++) {
      this.scenes[i] = this.defaultScenes[i];
    }
    // Reset checkbox states
    this.useLastStoredLevel = false;
    this.dontChangeExistingLevel = false;
    // Clear all error flags
    this.minLevelError = false;
    this.maxLevelError = false;
    this.powerOnLevelError = false;
    this.systemFailureLevelError = false;
    this.sceneErrors = new Array(16).fill(false);
    // Clear all modified fields
    this.modifiedFields.clear();
    // Recalculate percentages for default values
    this.calculateAllPercentages();
    this.updateFormControl();
    this.defaultColor = true;
    this.featureDefaults.setDali102and207ConfigurationDefaultColor(this.defaultColor);
    // Clear error state when resetting to defaults
    this.inputErrors.sendDali102and207ConfigurationInputError(false);
    // Save to localStorage after reset
    this.setConfigurationData();
    this.ref.detectChanges();
  }

  private setConfigurationData(): void {
    const data = this.appState.getConfigurationData() || { resp: {} };

    if (!data.resp) {
      data.resp = {};
    }

    // Always save ALL field values like DALI102 (no conditional logic)
    const daliConfig102and207Data: LegacyAny = {
      DimmingCurve: this.dimmingCurveType,
      MinLevel: this.minLevel,
      MaxLevel: this.maxLevel,
      PowerOnLevel: this.powerOnLevel,
      UseLastStoredLevel: this.useLastStoredLevel,
      SystemFailureLevel: this.systemFailureLevel,
      DontChangeExistingLevel: this.dontChangeExistingLevel,
      FadeRate: this.fadeRate,
      FadeTime: this.fadeTime,
      FastFadeTime: this.fastFadeTime,
      ExtendedFadeTime: this.extendedFadeTime,
      SelectedFadeType: this.selectedFadeType,
      OperatingMode: this.operatingMode
    };

    // Always include all scenes
    for (let i = 0; i < 16; i++) {
      daliConfig102and207Data[`Scene${i}`] = this.scenes[i];
    }

    data.resp.DaliConfiguration102and207 = daliConfig102and207Data;
    // Use centralized method to save feature data
    // This automatically saves to both ConfigurationData and SaveSetData
    this.appState.setFeatureData('DaliConfiguration102and207', daliConfig102and207Data);
  }
  private updateFormControl(): void {
    if (!this.formControl) {
      return;
    }

    // Calculate ExtendedFadeTimeBase and ExtendedFadeTimeMultiplier from extendedFadeTime
    this.calculateExtendedFadeTimeComponents();

    // Always include ALL field values like DALI102 (no conditional logic)
    const value: LegacyAny = {
      DimmingCurve: this.dimmingCurveType,
      MinLevel: this.minLevel,
      MaxLevel: this.maxLevel,
      PowerOnLevel: this.powerOnLevel,
      UseLastStoredLevel: this.useLastStoredLevel,
      SystemFailureLevel: this.systemFailureLevel,
      DontChangeExistingLevel: this.dontChangeExistingLevel,
      FadeRate: this.fadeRate,
      FadeTime: this.fadeTime,
      FastFadeTime: this.fastFadeTime,
      ExtendedFadeTime: this.extendedFadeTime,
      ExtendedFadeTimeBase: this.extendedFadeTimeBase,
      ExtendedFadeTimeMultiplier: this.extendedFadeTimeMultiplier,
      SelectedFadeType: this.selectedFadeType,
      OperatingMode: this.operatingMode
    };

    // Always include all scenes
    for (let i = 0; i < 16; i++) {
      value[`Scene${i}`] = this.scenes[i];
    }

    this.formControl.setValue(value);
  }

  /**
   * Calculate ExtendedFadeTimeBase and ExtendedFadeTimeMultiplier from extendedFadeTime
   * Encoding: base = Math.floor(value / 16), multiplier = value % 16
   */
  private calculateExtendedFadeTimeComponents(): void {
    if (this.extendedFadeTime === undefined || this.extendedFadeTime === null) {
      this.extendedFadeTimeBase = 0;
      this.extendedFadeTimeMultiplier = 0;
      return;
    }

    this.extendedFadeTimeBase = Math.floor(this.extendedFadeTime / 16);
    this.extendedFadeTimeMultiplier = this.extendedFadeTime % 16;
  }

  openInfo(e): void {
    e.stopPropagation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
